import { useEffect, useState } from 'react';
import { call, ApiError } from './api';

interface Retrieval {
  mode: 'retrieve';
  envelopeName: string;
  senderName: string;
  recipient: { name: string; email: string };
  completedAt: string;
  hasCertificate: boolean;
  documents: { id: string; name: string }[];
}

/**
 * The page a retrieval link opens.
 *
 * Reached from the completion email. The token behind it is scoped to
 * downloading only — it cannot consent, sign, or decline — so this page is
 * deliberately inert beyond fetching files.
 */
export default function DownloadPage({ token }: { token: string }) {
  const [data, setData] = useState<Retrieval | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    void call<Retrieval>(token, 'session')
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'This link could not be opened.'));
  }, [token]);

  async function open(kind: 'document' | 'certificate', documentId?: string) {
    setBusy(documentId ?? 'certificate');
    setError(null);
    try {
      const res = await call<{ url: string }>(token, kind,
        documentId ? { documentId } : {});
      window.location.href = res.url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not open that file.');
    } finally {
      setBusy(null);
    }
  }

  if (error && !data) return (
    <Shell>
      <h1>This link can’t be opened</h1>
      <div className="alert" role="alert">{error}</div>
      <p className="muted">
        Download links expire. Reply to the email that brought you here and ask for a new copy —
        there’s no charge.
      </p>
    </Shell>
  );

  if (!data) return <Shell><p className="muted">Loading…</p></Shell>;

  return (
    <Shell>
      <h1>Your signed documents</h1>
      <p className="muted">
        <strong>{data.envelopeName}</strong>
        {data.completedAt && <> · completed {new Date(data.completedAt).toLocaleDateString()}</>}
      </p>

      {error && <div className="alert" role="alert">{error}</div>}

      {data.documents.map((d) => (
        <button
          key={d.id} className="primary" style={{ marginBottom: '.5rem', minHeight: 50 }}
          disabled={busy !== null} onClick={() => void open('document', d.id)}
        >
          {busy === d.id ? 'Opening…' : `Download ${d.name}`}
        </button>
      ))}

      {data.hasCertificate && (
        <button
          style={{ width: '100%', minHeight: 50 }}
          disabled={busy !== null} onClick={() => void open('certificate')}
        >
          {busy === 'certificate' ? 'Opening…' : 'Download Certificate of Completion'}
        </button>
      )}

      <p className="muted" style={{ marginTop: '1.5rem', fontSize: '.85rem' }}>
        The Certificate records who signed, when, how each person was verified, and a SHA-256
        fingerprint of every file — so any later change to a document can be detected.
      </p>
      <p className="muted" style={{ fontSize: '.85rem' }}>
        <strong>Save these files somewhere you control.</strong> This link expires. After that,
        ask {data.senderName} for a copy — electronically or on paper, at no charge.
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="wrap" style={{ maxWidth: '32rem' }}>
      <div className="muted" style={{ fontSize: '.75rem', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: '1rem' }}>
        BeckerSign
      </div>
      {children}
    </main>
  );
}
